import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useThemeContext } from '../contexts/ThemeContext';
import { useNavLinksContext } from '../contexts/NavLinksContext';
import KPICard from '../components/dashboard/KPICard';
import { Select, Checkbox } from '../components/ui';
import { Loading } from '../components/utils';
import { useDashboard } from '../hooks/useDashboard';
import { useAppContext } from '../contexts/Storage';
import { useCurrency } from '../utils/globalCurrency';
import { useGetBalanceSheet, BalanceSheetApiResponse } from '../api/accounts';
import DemoBanner from '../components/dashboard/DemoBanner';
import { useRecords } from '../hooks/useRecords';
import { RecordItem } from '../models/RecordItem';
import { FROM_SETUP_KEY, isDemoSession } from '../onboarding/onboardingStorage';
import emptyFolderIcon from '../assets/images/empty_folder.png';
import copyIcon from '../assets/images/copy.png';
import computerIcon from '../assets/images/computer.png';
import GettingStartedChecklist from '../onboarding/GettingStartedChecklist';
import { useGetTransactions } from '../api/transactions';
import { TransactionFetchFilter } from '../models/TransactionFetchFilter';

const APP_URL = 'https://app.balancio.in';
const DISMISS_DESKTOP_PROMPT_KEY = 'dismiss_desktop_prompt';

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

// Helper to get default date range (all-time to today — matches KPI cumulative balances)
const getDefaultDateRange = () => {
  const today = new Date();

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    start_date: `1970-01-01 00:00:00`,
    end_date: `${formatDate(today)} 23:59:59`,
  };
};

const toNumber = (val: unknown): number => {
  if (typeof val === 'number' && !Number.isNaN(val)) return val;
  if (typeof val === 'string' && val.trim() !== '' && !Number.isNaN(Number(val))) return Number(val);
  return 0;
};

// Sum all numeric values in a records object (e.g. capital, liabilities)
const sumRecordValues = (obj: Record<string, unknown> | undefined): number => {
  if (!obj) return 0;
  return Object.values(obj).reduce((sum: number, val) => sum + toNumber(val), 0);
};

// Sum all numeric values in a potentially nested assets object
const sumAssetValues = (assets: Record<string, any> | undefined): number => {
  if (!assets) return 0;
  let total = 0;
  Object.values(assets).forEach(val => {
    if (typeof val === 'number' || typeof val === 'string') {
      total += toNumber(val);
    } else if (typeof val === 'object' && val !== null) {
      // Nested object like media: { cash: 1000, bank: 2000 }
      total += sumRecordValues(val as Record<string, unknown>);
    }
  });
  return total;
};

const DashboardPage: React.FC = () => {
  const { isDark } = useThemeContext();
  const { setNavLinks } = useNavLinksContext();
  const { user, selectedRecord: selectedRecordCtx, setSelectedRecord: setSelectedRecordCtx } = useAppContext();
  const { kpis, isLoading: isKpisLoading, fetchDashboardKpis } = useDashboard();
  const { fetchRecords } = useRecords();
  const navigate = useNavigate();
  const { format, formatWhole } = useCurrency();
  const [selectedRecord, setSelectedRecord] = useState<string>('all');
  const [recordsReady, setRecordsReady] = useState(false);

  const defaultDates = getDefaultDateRange();

  // Balance sheet data for the selected record
  const recordIdNum = selectedRecord !== 'all' ? parseInt(selectedRecord, 10) : selectedRecordCtx?.id;
  const {
    data: balanceSheetData,
    isLoading: isBalanceSheetLoading,
  } = useGetBalanceSheet({
    filters: {
      record_id: recordIdNum,
      user_id: user?.id,
      start_date: defaultDates.start_date,
      end_date: defaultDates.end_date,
    },
    enabled: !!recordIdNum && !!user?.id,
  });

  const balanceSheet = balanceSheetData?.data as BalanceSheetApiResponse | undefined;

  const txnProbeFilter = useMemo(() => {
    const filter = new TransactionFetchFilter();
    if (recordIdNum) filter.record_id = recordIdNum;
    filter.limit = 1;
    filter.page = 1;
    return filter;
  }, [recordIdNum]);

  const { data: txnProbe, isPending: isTxnProbePending } = useGetTransactions({
    filters: txnProbeFilter,
    enabled: !!recordIdNum && !!user?.id,
  });

  // Compute totals from balance sheet API response
  const capitalTotal = useMemo(() => {
    if (balanceSheet?.capital == null) return 0;
    if (typeof balanceSheet.capital === 'number') return balanceSheet.capital;
    return sumRecordValues(balanceSheet.capital as Record<string, unknown>);
  }, [balanceSheet]);

  const assetsTotal = useMemo(() => {
    if (!balanceSheet?.assets) return 0;
    return sumAssetValues(balanceSheet.assets);
  }, [balanceSheet]);

  const liabilitiesTotal = useMemo(() => {
    if (!balanceSheet?.liabilities) return 0;
    return sumRecordValues(balanceSheet.liabilities as Record<string, unknown>);
  }, [balanceSheet]);

  // Set navigation links for the Dashboard page
  useEffect(() => {
    setNavLinks({
      links: [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/records', label: 'Books' },
      ],
    });

    // Cleanup is handled by the NavLinksProvider default
  }, [setNavLinks]);

  // Fetch dashboard KPIs on component mount and after AI saves an event
  useEffect(() => {
    if (user?.id) {
      fetchDashboardKpis();
    }
    const refresh = () => {
      if (user?.id) fetchDashboardKpis();
    };
    window.addEventListener('balancio-books-updated', refresh);
    return () => window.removeEventListener('balancio-books-updated', refresh);
  }, [fetchDashboardKpis, user?.id]);

  // Load books so the overview has a record id. Demo still needs this;
  // it only skips the empty-books setup redirect.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    fetchRecords().then((result) => {
      if (cancelled) return;
      const list = result.success ? (result.data || []) : [];
      if (!isDemoSession() && list.length === 0) {
        navigate('/records?setup=1', { replace: true });
        return;
      }
      if (list.length > 0 && !selectedRecordCtx) {
        setSelectedRecordCtx(list[0]);
      }
      setRecordsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id, fetchRecords, navigate, selectedRecordCtx, setSelectedRecordCtx]);

  // If books fetch is slow or skipped, pick the first KPI record so loading can finish.
  useEffect(() => {
    if (selectedRecordCtx || !kpis.length) return;
    const kpi = kpis.find((item) => item.record_id);
    if (!kpi?.record_id) return;
    setSelectedRecordCtx(new RecordItem({
      id: kpi.record_id,
      record_name: kpi.record_name,
      record_description: kpi.record_description,
      record_currency_code: kpi.record_currency_code,
    }));
  }, [kpis, selectedRecordCtx, setSelectedRecordCtx]);

  // Extract unique records from KPIs
  const recordOptions = useMemo(() => {
    if (!kpis || kpis.length === 0) {
      return [
        { value: 'all', label: 'All Records' },
      ];
    }

    // Get unique records from KPIs
    const uniqueRecords = new Map<number, { id: number, name: string }>();

    kpis.forEach(kpi => {
      if (kpi.record_id && kpi.record_name) {
        if (!uniqueRecords.has(kpi.record_id)) {
          uniqueRecords.set(kpi.record_id, {
            id: kpi.record_id,
            name: kpi.record_name
          });
        }
      }
    });

    // Convert to dropdown options
    const recordOptionsArray = Array.from(uniqueRecords.values())
      .map(record => ({
        value: record.id.toString(),
        label: record.name
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return [
      { value: 'all', label: 'All Records' },
      ...recordOptionsArray
    ];
  }, [kpis]);

  // Filter KPIs based on selected record
  const filteredKpis = useMemo(() => {
    if (!kpis || kpis.length === 0) return [];

    if (selectedRecord === 'all') {
      return kpis;
    }

    return kpis.filter(kpi => kpi.record_id?.toString() === selectedRecord);
  }, [kpis, selectedRecord]);

  // Get KPIs for cards (top 5 or all if less than 5)
  const getKpiCards = () => {
    if (!filteredKpis || filteredKpis.length === 0) return [];

    return filteredKpis.slice(0, 5).map((kpi, index) => {
      // For each KPI, we need to format with its specific currency
      // Since format uses selected record's currency, we need a custom formatter for KPIs
      const currencySymbol = kpi.record_currency_code || '';
      const amount = kpi.balance_amount || 0;
      const formattedNumber = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Math.abs(amount));
      const sign = amount < 0 ? '-' : '';

      return {
        title: kpi.transaction_entity_name || 'Unknown',
        value: `${sign}${currencySymbol}${formattedNumber}`,
        change: kpi.difference_percentage || 0,
        isChangePositive: kpi.difference_type === 'POSITIVE',
        recordName: kpi.record_name || 'Record',
        currencyCode: kpi.record_currency_code || 'USD',
        trend: kpi.trend || [],
      };
    });
  };

  const kpiCards = getKpiCards();

  const hasActivity = useMemo(() => {
    const hasTransactions = (txnProbe?.pagination?.total ?? txnProbe?.transactions?.length ?? 0) > 0;
    const kpiActive = (kpis || []).some((kpi) => (kpi.balance_amount || 0) !== 0);
    return hasTransactions || kpiActive || capitalTotal !== 0 || assetsTotal !== 0 || liabilitiesTotal !== 0;
  }, [txnProbe, kpis, capitalTotal, assetsTotal, liabilitiesTotal]);

  const beginFirstTransaction = () => {
    sessionStorage.setItem(FROM_SETUP_KEY, '1');
  };

  const handleRecordChange = (value: string) => {
    setSelectedRecord(value);
  };

  const waitingForRecord = !recordIdNum && !recordsReady;
  const isLoading =
    isKpisLoading ||
    waitingForRecord ||
    (!!recordIdNum && (isBalanceSheetLoading || isTxnProbePending));

  // ---- Mobile Desktop Prompt Dialog ----
  const isMobile = useIsMobile();
  const [showMobileDialog, setShowMobileDialog] = useState(() => {
    // On mobile, only show if not previously dismissed
    if (window.innerWidth < 768) {
      return !localStorage.getItem(DISMISS_DESKTOP_PROMPT_KEY);
    }
    return false;
  });

  const [dontShowAgain, setDontShowAgain] = useState(false);

  const dismissPrompt = useCallback(() => {
    if (dontShowAgain) {
      localStorage.setItem(DISMISS_DESKTOP_PROMPT_KEY, 'true');
    }
    setShowMobileDialog(false);
  }, [dontShowAgain]);

  const handleCopyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(APP_URL);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = APP_URL;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    dismissPrompt();
  }, [dismissPrompt]);

  const handleNoThanks = useCallback(() => {
    dismissPrompt();
  }, [dismissPrompt]);

    return (
    <div data-tour="dashboard-main" className={`h-shink-from-navbar h-fill-from-navbar pl-2 text-dark overflow-y-scroll`}>
      <div>
        <DemoBanner />
        {/* Mobile Desktop Prompt Dialog */}
        {isMobile && showMobileDialog && (
          <>
            {/* Backdrop overlay */}
            <div
              className="fixed inset-0 bg-gray-700 bg-opacity-50 z-40"
              onClick={handleNoThanks}
            />
            {/* Dialog */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
              <div className="bg-light rounded-2xl shadow-xl max-w-sm w-full mx-auto p-6">
                {/* Icon */}
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-grey_light flex items-center justify-center">
                    <img src={computerIcon} alt="Desktop" className="w-8 h-8" />
                  </div>
                </div>
                {/* Title */}
                <h3 className="text-lg font-semibold text-dark text-center mb-2">
                  Desktop Experience Recommended
                </h3>
                {/* Message */}
                <p className="text-grey_dark text-sm text-center mb-6 leading-relaxed">
                  Please view on desktop for a better experience.
                  <br />
                  <span className="text-xs text-grey_dark mt-1 block">
                    Copy the link to open on your computer
                  </span>
                </p>
                {/* URL display */}
                <div className="flex items-center justify-between bg-bg_grey_light rounded-lg px-4 py-3 mb-4">
                  <span className="text-sm text-dark font-medium truncate mr-2">{APP_URL}</span>
                  <button
                    onClick={handleCopyUrl}
                    className="flex-shrink-0 flex items-center gap-1 text-primary text-sm font-medium hover:text-primary_variant transition-colors"
                  >
                    <img src={copyIcon} alt="Copy" className="w-4 h-4" />
                    Copy
                  </button>
                </div>
                                {/* Don't show again checkbox */}
                <div className="flex justify-center mb-4">
                  <Checkbox
                    id="dont-show-again"
                    checked={dontShowAgain}
                    onChange={setDontShowAgain}
                    label="Don't show again"
                  />
                </div>
                {/* No thanks button */}
                <button
                  onClick={handleNoThanks}
                  className="w-full py-2.5 rounded-lg border border-grey_light text-grey_dark text-sm font-medium hover:bg-bg_grey_light transition-colors"
                >
                  No, thanks
                </button>
              </div>
            </div>
          </>
        )}

        {isLoading ? (
          <Loading width="30px" height="30px" className="mx-2 mb-4" />
        ) : !hasActivity ? (
          <div
            data-tour="dashboard-kpis"
            className="bg-light rounded-lg border border-grey_light mb-2 min-h-[calc(100vh-80px)] flex flex-col"
          >
            <GettingStartedChecklist variant="embedded" />
            <div className="px-6 py-4 border-b border-grey_light text-left">
              <h3 className="text-xl text-dark">Overview</h3>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
              <img
                src={emptyFolderIcon}
                alt=""
                className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 opacity-40"
              />
              <p className="text-dark font-medium mb-1">No figures yet</p>
              <p className="text-sm text-grey_dark mb-6 max-w-md mx-auto">
                They appear after you save one sale or expense.
              </p>
              <Link
                to="/records/transactions/new"
                onClick={beginFirstTransaction}
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-md bg-primary text-white font-medium"
              >
                Add first transaction
              </Link>
            </div>
          </div>
        ) : (
          <>
        <div className="bg-light rounded-lg border border-grey_light mb-4">
          <GettingStartedChecklist variant="embedded" />
        </div>
        {/* Header and title */}
        <div className="p-6 pb-3 bg-light rounded-t-lg mb-4 flex justify-between items-center">
          <h3 className="text-xl mx-4">Overview</h3>
          {/* Dropdown to select record */}
          <div className="relative" data-tour="dashboard-record-select">
            <Select
              options={recordOptions}
              value={selectedRecord}
              onChange={handleRecordChange}
              placeholder="Select record"
              className="w-48"
              disabled={isKpisLoading || kpis.length === 0}
            />
          </div>
        </div>
            {/* Balances */}
            <div data-tour="dashboard-balances" className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6 mb-4 text-center">
              {/* Overview Cards */}
              <div className={`p-4 mx-8 md:mx-2 lg:mx-0 rounded-lg bg-light border border-secondary`}>
                <h3 className="text-md mb-2">Capital</h3>
                <hr className="my-2 border-secondary" />
                <p
                  className="text-lg font-meduim text-dark dashboard-figure"
                  title={formatWhole(capitalTotal)}
                >
                  {formatWhole(capitalTotal)}
                </p>
              </div>

              <div className={`p-4 mx-8 md:mx-2 lg:mx-0 rounded-lg bg-light border border-primary`}>
                <h3 className="text-md mb-2">Assets</h3>
                <hr className="my-2 border-primary" />
                <p
                  className="text-lg font-meduim text-dark dashboard-figure"
                  title={formatWhole(assetsTotal)}
                >
                  {formatWhole(assetsTotal)}
                </p>
              </div>

              <div className={`p-4 mx-8 md:mx-2 lg:mx-0 rounded-lg bg-light border border-secondary`}>
                <h3 className="text-md mb-2">Liabilities</h3>
                <hr className="my-2 border-secondary" />
                <p
                  className="text-lg font-meduim text-dark dashboard-figure"
                  title={formatWhole(liabilitiesTotal)}
                >
                  {formatWhole(liabilitiesTotal)}
                </p>
              </div>
            </div>

            {/* KPI Cards */}
            <div data-tour="dashboard-kpis" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mb-4 lg:me-1">
              {kpiCards.length > 0 ? (
                kpiCards.map((kpi, index) => (
                  <KPICard
                    key={index}
                    props={{
                      title: kpi.title,
                      value: kpi.value,
                      change: kpi.change,
                      isChangePositive: kpi.isChangePositive,
                      recordName: kpi.recordName,
                      trend: kpi.trend,
                    }}
                  />
                ))
              ) : (
                <div className="col-span-2 text-center py-8">
                  <img
                    src={emptyFolderIcon}
                    alt="No data"
                    className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 opacity-50"
                  />
                  <p className="text-dark font-medium mb-1">Nothing here yet</p>
                  <p className="text-grey_dark mb-6 px-4">
                    Add your first sale or expense. The overview fills in after that.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link
                      to="/records?setup=1"
                      className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary text-white font-medium"
                    >
                      Create books
                    </Link>
                    <Link
                      to="/records/transactions/new"
                      onClick={beginFirstTransaction}
                      className="inline-flex items-center justify-center px-4 py-2 rounded-md border border-primary text-primary font-medium"
                    >
                      Add first transaction
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;